import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Col,

  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  LoadingOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import { AdminLayout } from '../../components/AdminLayout';
import api from '../../app/api';
import { BASE_URL } from '../../app/config';
import { useTranslation } from '../../shared/hooks/useTranslation';
import style from './SpecialistsManagement.module.scss';

type Specialist = {
  id: number;
  // site_id — публичный ID (Excel, колонка A), показывается в ссылке /doctor/id/{site_id}.
  // doktor_id — DR-код записи в МИС (= services.doktor_id, напр. UM0168).
  site_id?: string;
  doktor_id?: string;
  full_name: string;
  specialization: string;
  category: string;
  about_doctor: string;
  contact_info: string;
  iin: string;
  experience_years: number;
  education: string;
  biography: string;
  phone: string;
  email: string;
  photo_url: string;
  schedule: string;
  patronymic_ru: string;
  last_name_ru: string;
  first_name_ru: string;
  centers_ru: string;
  kads_ru: string;
  department_ru: string;
  specialty_ru: string;
  specializations_ru: string;
  languages_ru: string;
  reviews_link_ru: string;
  review_form_link_ru: string;
  degree_ru: string;
  experience_ru: string;
  patronymic_kz: string;
  last_name_kz: string;
  first_name_kz: string;
  centers_kz: string;
  kads_kz: string;
  department_kz: string;
  specialty_kz: string;
  specializations_kz: string;
  languages_kz: string;
  reviews_link_kz: string;
  review_form_link_kz: string;
  degree_kz: string;
  experience_kz: string;
  patronymic_en: string;
  last_name_en: string;
  first_name_en: string;
  centers_en: string;
  kads_en: string;
  department_en: string;
  specialty_en: string;
  specializations_en: string;
  languages_en: string;
  reviews_link_en: string;
  review_form_link_en: string;
  degree_en: string;
  experience_en: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

type SpecialistFormValues = Omit<Specialist, 'id' | 'created_at' | 'updated_at'>;

const MAX_PHOTO_SIZE_MB = 5;
const MAX_PHOTO_SIZE_BYTES = MAX_PHOTO_SIZE_MB * 1024 * 1024;

const resolvePhotoUrl = (photoUrl?: string) => {
  if (!photoUrl) return undefined;
  if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
    return photoUrl;
  }
  if (photoUrl.startsWith('/')) {
    return `${BASE_URL}${photoUrl}`;
  }
  return `${BASE_URL}/${photoUrl}`;
};

const defaultFormValues: SpecialistFormValues = {
  doktor_id: '',
  full_name: '',
  specialization: '',
  category: '',
  about_doctor: '',
  contact_info: '',
  iin: '',
  experience_years: 0,
  education: '',
  biography: '',
  phone: '',
  email: '',
  photo_url: '',
  schedule: '',
  patronymic_ru: '',
  last_name_ru: '',
  first_name_ru: '',
  centers_ru: '',
  kads_ru: '',
  department_ru: '',
  specialty_ru: '',
  specializations_ru: '',
  languages_ru: '',
  reviews_link_ru: '',
  review_form_link_ru: '',
  degree_ru: '',
  experience_ru: '',
  patronymic_kz: '',
  last_name_kz: '',
  first_name_kz: '',
  centers_kz: '',
  kads_kz: '',
  department_kz: '',
  specialty_kz: '',
  specializations_kz: '',
  languages_kz: '',
  reviews_link_kz: '',
  review_form_link_kz: '',
  degree_kz: '',
  experience_kz: '',
  patronymic_en: '',
  last_name_en: '',
  first_name_en: '',
  centers_en: '',
  kads_en: '',
  department_en: '',
  specialty_en: '',
  specializations_en: '',
  languages_en: '',
  reviews_link_en: '',
  review_form_link_en: '',
  degree_en: '',
  experience_en: '',
  is_active: true,
};

/** Итоговое ФИО: вручную или из фамилии, имени, отчества (RU). */
function buildFullName(v: Partial<SpecialistFormValues>): string {
  const manual = (v.full_name || '').trim();
  if (manual) {
    return manual;
  }
  return [v.last_name_ru, v.first_name_ru, v.patronymic_ru]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Краткая специализация для API: внутр. поле, специальность (RU) или первая из списка (RU). */
function buildSpecialization(v: Partial<SpecialistFormValues>): string {
  const manual = (v.specialization || '').trim();
  if (manual) {
    return manual;
  }
  const fromSpecialty = (v.specialty_ru || v.specialty_kz || v.specialty_en || '').trim();
  if (fromSpecialty) {
    return fromSpecialty;
  }
  return (
    (v.specializations_ru || '')
      .split(',')
      .map((s) => s.trim())
      .find(Boolean) || ''
  );
}

/** Сортировка по умолчанию (п. 2.3: список + фильтры). */
const DEFAULT_SPECIALISTS_SORT = 'updated_desc';

function LocalizationFields({ lang }: { lang: 'ru' | 'kz' | 'en' }) {
  const form = Form.useFormInstance();
  const { t } = useTranslation();

  const fioValidator = async () => {
    if (lang !== 'ru') {
      return Promise.resolve();
    }
    const v = form.getFieldsValue(true) as SpecialistFormValues;
    if (!buildFullName(v)) {
      return Promise.reject(new Error(t.specMgmtFioRequiredAlert));
    }
    return Promise.resolve();
  };

  const specValidator = async () => {
    if (lang !== 'ru') {
      return Promise.resolve();
    }
    const v = form.getFieldsValue(true) as SpecialistFormValues;
    if (!buildSpecialization(v)) {
      return Promise.reject(new Error(t.specMgmtSpecRequiredAlert));
    }
    return Promise.resolve();
  };

  const fioRules =
    lang === 'ru'
      ? [{ validator: fioValidator }]
      : undefined;
  const specRules =
    lang === 'ru'
      ? [{ validator: specValidator }]
      : undefined;

  return (
    <div className={style.langSection}>
      <Form.Item name={`last_name_${lang}`}
        label={t.specMgmtLastName}
        dependencies={lang === 'ru' ? ['full_name', 'last_name_ru', 'first_name_ru', 'patronymic_ru'] : undefined}
        rules={fioRules}
        extra={lang === 'ru' ? t.specMgmtLastNameHelp : undefined}
      >
        <Input />
      </Form.Item>
      <Form.Item name={`first_name_${lang}`} label={t.specMgmtFirstName}>
        <Input />
      </Form.Item>
      <Form.Item name={`patronymic_${lang}`} label={t.specMgmtPatronymic}>
        <Input />
      </Form.Item>
      <Form.Item name={`centers_${lang}`} label={t.specMgmtCenters}>
        <Input />
      </Form.Item>
      <Form.Item name={`kads_${lang}`} label={t.specMgmtKads}>
        <Input />
      </Form.Item>
      <Form.Item name={`department_${lang}`} label={t.specMgmtDepartment}>
        <Input />
      </Form.Item>
      <Form.Item name={`specialty_${lang}`}
        label={t.specMgmtSpecialty}
        dependencies={
          lang === 'ru' ? ['specialization', 'specialty_ru', 'specializations_ru', 'specialty_kz', 'specialty_en'] : undefined
        }
        rules={specRules}
        extra={lang === 'ru' ? t.specMgmtSpecialtyHelp : undefined}
      >
        <Input />
      </Form.Item>
      <Form.Item name={`specializations_${lang}`} label={t.specMgmtSpecializations} extra={t.specMgmtMultiValuesHint}>
        <Input placeholder={t.specMgmtSpecializationsPh} />
      </Form.Item>
      <Form.Item name={`languages_${lang}`} label={t.specMgmtLanguages} extra={t.specMgmtMultiValuesHint}>
        <Input placeholder={t.specMgmtLanguagesPh} />
      </Form.Item>
      <Form.Item name={`reviews_link_${lang}`} label={t.specMgmtReviewsLink}>
        <Input placeholder="https://..." />
      </Form.Item>
      <Form.Item name={`review_form_link_${lang}`} label={t.specMgmtReviewFormLink}>
        <Input placeholder="https://..." />
      </Form.Item>
      <Form.Item name={`degree_${lang}`} label={t.specMgmtDegree}>
        <Input />
      </Form.Item>
      <Form.Item name={`experience_${lang}`} label={t.specMgmtExperience} style={{ marginBottom: 0 }}>
        <Input.TextArea rows={2} />
      </Form.Item>
    </div>
  );
}

export function SpecialistsManagement() {
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>(DEFAULT_SPECIALISTS_SORT);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);
  const [editingSpecialist, setEditingSpecialist] = useState<Specialist | null>(null);
  const [form] = Form.useForm<SpecialistFormValues>();
  const { t } = useTranslation();

  const fetchSpecialists = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/specialists');
      setSpecialists(response.data?.data || []);
    } catch (error: any) {
      message.error(error?.response?.data?.error || t.specMgmtErrorLoad);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecialists();
  }, []);

  const openCreateModal = () => {
    setEditingSpecialist(null);
    form.setFieldsValue(defaultFormValues);
    setIsModalOpen(true);
  };

  const openEditModal = (specialist: Specialist) => {
    setEditingSpecialist(specialist);
    form.setFieldsValue({
      ...defaultFormValues,
      ...specialist,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    form.resetFields();
    setIsModalOpen(false);
    setEditingSpecialist(null);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);
      const finalFullName = buildFullName(values);
      const finalSpecialization = buildSpecialization(values);

      const payload = {
        ...values,
        full_name: finalFullName,
        specialization: finalSpecialization,
      };

      if (editingSpecialist) {
        await api.put(`/admin/specialists/${editingSpecialist.id}`, payload);
        message.success(t.specMgmtUpdated);
      } else {
        await api.post('/admin/specialists', payload);
        message.success(t.specMgmtAdded);
      }

      closeModal();
      fetchSpecialists();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.response?.data?.error || t.specMgmtErrorSave);
    } finally {
      setIsSaving(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    const isAllowedType = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isAllowedType) {
      message.error(t.specMgmtPhotoFormatError);
      return false;
    }

    if (file.size > MAX_PHOTO_SIZE_BYTES) {
      message.error(t.specMgmtPhotoSizeError.replace('{mb}', String(MAX_PHOTO_SIZE_MB)));
      return false;
    }

    const formData = new FormData();
    formData.append('photo', file);

    try {
      setIsPhotoUploading(true);
      const response = await api.post('/admin/specialists/upload-photo', formData);

      form.setFieldValue('photo_url', response.data?.path || response.data?.url || '');
      message.success(t.specMgmtPhotoUploaded);
    } catch (error: any) {
      message.error(error?.response?.data?.error || t.specMgmtPhotoUploadError);
    } finally {
      setIsPhotoUploading(false);
    }

    return false;
  };

  const handleDelete = async (id: number) => {
    try {
      await api.delete(`/admin/specialists/${id}`);
      message.success(t.specMgmtDeleted);
      fetchSpecialists();
    } catch (error: any) {
      message.error(error?.response?.data?.error || t.specMgmtErrorDelete);
    }
  };

  const handleExcelImport = async (file: File) => {
    const lowerName = file.name.toLowerCase();
    if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
      message.error(t.specMgmtExcelOnly);
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      setImporting(true);
      const response = await api.post('/admin/upload-specialists', formData);

      const details = response.data?.details || {};
      message.success(
        response.data?.message ||
          `${t.specMgmtAdded}: +${details.rowsInserted || 0} / ${details.rowsSkipped || 0}`,
      );
      await fetchSpecialists();
    } catch (error: any) {
      message.error(error?.response?.data?.error || t.specMgmtExcelError);
    } finally {
      setImporting(false);
    }
  };

  const uploadProps: UploadProps = {
    accept: '.xlsx,.xls',
    showUploadList: false,
    maxCount: 1,
    beforeUpload: (file) => {
      handleExcelImport(file as unknown as File);
      return false;
    },
  };

  const handleExcelExport = async () => {
    try {
      setExporting(true);
      const response = await api.get('/admin/export-specialists', { responseType: 'blob' });

      const disposition = response.headers?.['content-disposition'] as string | undefined;
      const match = disposition ? /filename="?([^"]+)"?/i.exec(disposition) : null;
      const filename = match?.[1] || `specialists_${new Date().toISOString().slice(0, 10)}.xlsx`;

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success(t.specMgmtExportSuccess);
    } catch (error: any) {
      message.error(error?.response?.data?.error || t.specMgmtExportError);
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      title: t.specMgmtColSpecialist,
      key: 'specialist',
      render: (_: unknown, row: Specialist) => {
        const spec = row.specialty_ru || row.specialization || '';
        return (
          <div className={style.specialistCell}>
            <Avatar
              size={32}
              src={resolvePhotoUrl(row.photo_url)}
              icon={!row.photo_url ? <UserOutlined /> : undefined}
            />
            <div className={style.specialistInfo}>
              <span className={style.specialistName}>{row.full_name || '—'}</span>
              {spec && <span className={style.specialistSpecialty}>{spec}</span>}
            </div>
          </div>
        );
      },
    },
    {
      // Диагностическая колонка: публичный ID из ссылки и DR-код записи в МИС.
      title: 'ID (ссылка / МИС)',
      key: 'ids',
      render: (_: unknown, row: Specialist) => (
        <span className={style.idsCell}>
          {row.site_id || '—'} <span style={{ color: 'var(--text-muted)' }}>/</span> {row.doktor_id || '—'}
        </span>
      ),
    },
    {
      title: t.specMgmtColCategory,
      dataIndex: 'category',
      key: 'category',
      render: (value: string) => value ? <Tag>{value}</Tag> : '—',
    },
    {
      title: t.specMgmtColExperience,
      dataIndex: 'experience_years',
      key: 'experience_years',
      render: (years: number) => years ? `${years} ${t.specMgmtYears}` : '—',
    },
    {
      title: t.specMgmtColContacts,
      key: 'contacts',
      render: (_: unknown, row: Specialist) => {
        const parts = [row.phone, row.email].filter(Boolean);
        return parts.length ? (
          <span className={style.contactsCell}>{parts.join(' · ')}</span>
        ) : '—';
      },
    },
    {
      title: t.specMgmtColDepartment,
      dataIndex: 'department_ru',
      key: 'department',
      render: (value: string) => value || '—',
    },
    {
      title: t.specMgmtColStatus,
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) =>
        isActive ? <Tag color="green">{t.specMgmtStatusActive}</Tag> : <Tag color="red">{t.specMgmtStatusInactive}</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 72,
      fixed: 'right' as const,
      render: (_: unknown, row: Specialist) => (
        <Space>
          <Tooltip title={t.editButton}>
            <Button icon={<EditOutlined />} size="small" onClick={() => openEditModal(row)} />
          </Tooltip>
          <Popconfirm
            title={t.specMgmtConfirmDelete}
            description={t.specMgmtConfirmDeleteDesc}
            okText={t.deleteButton}
            cancelText={t.cancelButton}
            onConfirm={() => handleDelete(row.id)}
          >
            <Tooltip title={t.deleteButton}>
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const primarySpecializationLabel = (item: Specialist) =>
    (item.specialty_ru || item.specialization || '').trim();

  const itemMatchesSpecFilter = (item: Specialist, filter: string) => {
    if (filter === 'all') {
      return true;
    }
    const primary = (item.specialization || item.specialty_ru || '').trim();
    if (primary === filter) {
      return true;
    }
    const fromList = (item.specializations_ru || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    if (fromList.some((p) => p === filter)) {
      return true;
    }
    return fromList.some((p) => p.toLowerCase() === filter.toLowerCase()) || primary.toLowerCase() === filter.toLowerCase();
  };

  const specializationOptions = useMemo(() => {
    const unique = new Set<string>();
    specialists.forEach((item) => {
      const primary = (item.specialization || item.specialty_ru || '').trim();
      if (primary) {
        unique.add(primary);
      }
      (item.specializations_ru || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((part) => unique.add(part));
    });
    return Array.from(unique)
      .sort((a, b) => a.localeCompare(b, 'ru'))
      .map((value) => ({ label: value, value }));
  }, [specialists]);

  const specSortKey = (item: Specialist) => (primarySpecializationLabel(item) || item.specialization || '').toLowerCase();

  const filteredSpecialists = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const data = specialists.filter((item) => {
      if (categoryFilter !== 'all' && item.category !== categoryFilter) {
        return false;
      }

      if (!itemMatchesSpecFilter(item, specializationFilter)) {
        return false;
      }

      if (!normalizedQuery) {
        return true;
      }

      const haystack = [
        item.full_name,
        item.specialization,
        item.specialty_ru,
        item.specialty_kz,
        item.specialty_en,
        item.specializations_ru,
        item.specializations_kz,
        item.specializations_en,
        item.category,
        item.phone,
        item.email,
        item.education,
        item.about_doctor,
        item.last_name_ru,
        item.first_name_ru,
        item.patronymic_ru,
        item.iin,
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(normalizedQuery);
    });

    return data.sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':
          return (a.full_name || '').localeCompare(b.full_name || '', 'ru');
        case 'name_desc':
          return (b.full_name || '').localeCompare(a.full_name || '', 'ru');
        case 'spec_asc':
          return specSortKey(a).localeCompare(specSortKey(b), 'ru');
        case 'spec_desc':
          return specSortKey(b).localeCompare(specSortKey(a), 'ru');
        case 'experience_desc':
          return (b.experience_years || 0) - (a.experience_years || 0);
        case 'experience_asc':
          return (a.experience_years || 0) - (b.experience_years || 0);
        case 'updated_asc':
          return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
        case 'updated_desc':
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      }
    });
  }, [categoryFilter, searchQuery, sortBy, specializationFilter, specialists]);

  const hasActiveFilters =
    Boolean(searchQuery.trim()) ||
    specializationFilter !== 'all' ||
    categoryFilter !== 'all' ||
    sortBy !== DEFAULT_SPECIALISTS_SORT;

  const handleResetFilters = () => {
    setSearchQuery('');
    setSpecializationFilter('all');
    setCategoryFilter('all');
    setSortBy(DEFAULT_SPECIALISTS_SORT);
  };

  return (
    <AdminLayout>
      <div className={style.container}>
        <div className={style.headerRow}>
          <div className={style.titleRow}>
            <h1 className={style.title}>{t.specMgmtTitle}</h1>
            <span className={style.subtitle}>
              {hasActiveFilters
                ? `${t.specMgmtFoundLabel} ${filteredSpecialists.length} ${t.specMgmtFoundOf} ${specialists.length}`
                : `${t.specMgmtTotalLabel} ${specialists.length}`}
            </span>
          </div>

          <Space>
            <Upload {...uploadProps}>
              <Button icon={<UploadOutlined />} loading={importing}>
                {t.specMgmtImportExcel}
              </Button>
            </Upload>
            <Button
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExcelExport}
              disabled={specialists.length === 0}
            >
              {t.specMgmtExportExcel}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              {t.specMgmtAddBtn}
            </Button>
          </Space>
        </div>

        <Card size="small" className={style.filtersCard}>
          <Row gutter={[12, 12]} align="middle">
            <Col flex="auto">
              <Input.Search
                allowClear
                placeholder={t.specMgmtSearchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Col>
            <Col>
              <Select
                value={specializationFilter}
                onChange={setSpecializationFilter}
                style={{ width: 220 }}
                options={[
                  { label: t.specMgmtFilterSpecAll, value: 'all' },
                  ...specializationOptions,
                ]}
                showSearch
                optionFilterProp="label"
                placeholder={t.specMgmtSpecializations}
              />
            </Col>
            <Col>
              <Select
                value={categoryFilter}
                onChange={setCategoryFilter}
                style={{ width: 170 }}
                options={[
                  { label: t.specMgmtFilterCatAll, value: 'all' },
                  { label: t.specMgmtFilterCatChild, value: 'детский' },
                  { label: t.specMgmtFilterCatAdult, value: 'взрослый' },
                ]}
                placeholder={t.specMgmtFieldCategory}
              />
            </Col>
            <Col>
              <Select
                value={sortBy}
                onChange={setSortBy}
                style={{ width: 220 }}
                options={[
                  { label: t.specMgmtSortRecent, value: 'updated_desc' },
                  { label: t.specMgmtSortOldest, value: 'updated_asc' },
                  { label: t.specMgmtSortNameAsc, value: 'name_asc' },
                  { label: t.specMgmtSortNameDesc, value: 'name_desc' },
                  { label: t.specMgmtSortSpecAsc, value: 'spec_asc' },
                  { label: t.specMgmtSortSpecDesc, value: 'spec_desc' },
                  { label: t.specMgmtSortExpDesc, value: 'experience_desc' },
                  { label: t.specMgmtSortExpAsc, value: 'experience_asc' },
                ]}
              />
            </Col>
            {hasActiveFilters && (
              <Col>
                <Button type="link" size="small" onClick={handleResetFilters}>
                  {t.resetButton}
                </Button>
              </Col>
            )}
          </Row>
        </Card>

        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="id"
            className={style.table}
            loading={loading}
            dataSource={filteredSpecialists}
            columns={columns}
            pagination={{
              pageSize: 15,
              showSizeChanger: true,
              pageSizeOptions: ['10', '15', '30', '50'],
              showTotal: (total, range) => `${range[0]}–${range[1]} ${t.specMgmtFoundOf} ${total}`,
            }}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </Card>
      </div>

      <Modal
        title={editingSpecialist ? t.specMgmtModalEdit : t.specMgmtModalNew}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={handleSave}
        okText={editingSpecialist ? t.saveButton : t.addButton}
        cancelText={t.cancelButton}
        confirmLoading={isSaving}
        destroyOnClose
        width={720}
        className={style.modal}
      >
        <Form form={form} layout="vertical" initialValues={defaultFormValues} className={style.form}>
          <Tabs
            defaultActiveKey="general"
            items={[
              {
                key: 'general',
                label: t.specMgmtTabGeneral,
                children: (
                  <div className={style.tabContent}>
                    <Row gutter={16}>
                      <Col xs={24} md={16} className={style.fieldsCol}>
                        <Card size="small" title={t.specMgmtSectionBasic} className={style.sectionCard}>
                          <div className={style.statusRow}>
                            <span className={style.statusLabel}>{t.specMgmtFieldStatus}</span>
                            <Form.Item name="is_active" valuePropName="checked" noStyle>
                              <Switch size="small" />
                            </Form.Item>
                            <span className={style.statusHint}>{t.specMgmtStatusHint}</span>
                          </div>
                          <Form.Item
                            name="full_name"
                            label={t.specMgmtFullNameManual}
                            extra={t.specMgmtFullNameManualHint}
                          >
                            <Input allowClear placeholder={t.specMgmtFullNamePh} />
                          </Form.Item>
                          <Form.Item
                            name="specialization"
                            label={t.specMgmtBriefSpecLabel}
                            extra={t.specMgmtBriefSpecHint}
                          >
                            <Input allowClear placeholder={t.specMgmtBriefSpecPh} />
                          </Form.Item>
                          <Form.Item
                            name="doktor_id"
                            label="МИС ID (Акгюн)"
                            extra="DR-код записи в МИС, напр. UM0168. Совпадает с doktor_id в каталоге услуг. Заполняется автопривязкой при импорте, но можно указать вручную."
                          >
                            <Input allowClear placeholder="напр. UM0168" />
                          </Form.Item>
                          <Form.Item name="category" label={t.specMgmtFieldCategory} rules={[{ required: true, message: t.specMgmtCategorySelect }]}>
                            <Select
                              allowClear
                              options={[
                                { value: 'детский', label: t.specMgmtCategoryChild },
                                { value: 'взрослый', label: t.specMgmtCategoryAdult },
                              ]}
                              placeholder={t.specMgmtCategoryPh}
                            />
                          </Form.Item>
                          <Form.Item name="iin" label={t.specMgmtFieldIin} extra={t.specMgmtFieldIinHint}>
                            <Input maxLength={12} />
                          </Form.Item>
                          <Form.Item name="experience_years" label={t.specMgmtExpYears}>
                            <InputNumber min={0} max={80} style={{ width: '100%' }} />
                          </Form.Item>
                        </Card>

                        <Card size="small" title={t.specMgmtSectionBio} className={style.sectionCard}>
                          <Form.Item name="about_doctor" label={t.specMgmtAboutDoctor}>
                            <Input.TextArea rows={3} placeholder={t.specMgmtAboutDoctorPh} />
                          </Form.Item>
                          <Form.Item name="education" label={t.specMgmtEducation}>
                            <Input.TextArea rows={2} placeholder={t.specMgmtEducationPh} />
                          </Form.Item>
                          <Form.Item name="biography" label={t.specMgmtBiography} style={{ marginBottom: 0 }}>
                            <Input.TextArea rows={2} placeholder={t.specMgmtBiographyPh} />
                          </Form.Item>
                        </Card>
                      </Col>

                      <Col xs={24} md={8} className={style.photoCol}>
                        <Card size="small" title={t.specMgmtSectionPhoto} className={style.sectionCard}>
                          <div className={style.photoSection}>
                            <div className={style.photoAvatarWrap} aria-busy={isPhotoUploading}>
                              <Form.Item noStyle shouldUpdate>
                                {() => {
                                  const photoUrl = form.getFieldValue('photo_url');
                                  return (
                                    <Avatar
                                      size={100}
                                      src={resolvePhotoUrl(photoUrl)}
                                      icon={!photoUrl ? <UserOutlined /> : undefined}
                                      className={style.photoAvatar}
                                    />
                                  );
                                }}
                              </Form.Item>
                              {isPhotoUploading && (
                                <div className={style.photoLoadingOverlay} aria-live="polite" role="status">
                                  <div className={style.photoLoadingRing} aria-hidden>
                                    <LoadingOutlined className={style.photoLoadingIcon} spin />
                                  </div>
                                  <span className={style.photoLoadingText}>{t.specMgmtPhotoLoading}</span>
                                  <span className={style.photoLoadingSub}>{t.specMgmtPhotoLoadingPlease}</span>
                                </div>
                              )}
                            </div>

                            <Upload
                              accept="image/png,image/jpeg"
                              maxCount={1}
                              showUploadList={false}
                              disabled={isPhotoUploading}
                              beforeUpload={(file) => handlePhotoUpload(file as unknown as File)}
                            >
                              <Button
                                type="default"
                                icon={isPhotoUploading ? <LoadingOutlined spin /> : <UploadOutlined />}
                                block
                                disabled={isPhotoUploading}
                              >
                                {isPhotoUploading ? t.specMgmtPhotoUploading : t.specMgmtPhotoUpload}
                              </Button>
                            </Upload>

                            <Form.Item noStyle shouldUpdate>
                              {() => {
                                const photoUrl = form.getFieldValue('photo_url');
                                if (!photoUrl) return null;
                                return (
                                  <Button
                                    danger
                                    size="small"
                                    block
                                    disabled={isPhotoUploading}
                                    onClick={() => form.setFieldValue('photo_url', '')}
                                  >
                                    {t.deleteButton}
                                  </Button>
                                );
                              }}
                            </Form.Item>

                            <div className={style.photoHint}>{t.specMgmtPhotoHint}</div>
                          </div>
                        </Card>
                      </Col>
                    </Row>

                    <Form.Item name="photo_url" hidden>
                      <Input />
                    </Form.Item>
                  </div>
                ),
              },
              {
                key: 'contacts',
                label: t.specMgmtTabContacts,
                children: (
                  <div className={style.tabContent}>
                    <Card size="small" title={t.specMgmtSectionContacts} className={style.sectionCard}>
                      <Form.Item name="phone" label={t.specMgmtPhone}>
                        <Input placeholder={t.specMgmtPhonePh} />
                      </Form.Item>
                      <Form.Item name="email" label={t.specMgmtEmail} rules={[{ type: 'email', message: t.specMgmtEmailInvalid }]}>
                        <Input placeholder={t.specMgmtEmailPh} />
                      </Form.Item>
                      <Form.Item name="contact_info" label={t.specMgmtAdditionalContacts} style={{ marginBottom: 0 }}>
                        <Input.TextArea rows={2} />
                      </Form.Item>
                    </Card>

                    <Card size="small" title={t.specMgmtSectionSchedule} className={style.sectionCard}>
                      <Form.Item name="schedule" style={{ marginBottom: 0 }}>
                        <Input.TextArea rows={3} placeholder={t.specMgmtSchedulePh} />
                      </Form.Item>
                    </Card>
                  </div>
                ),
              },
              {
                key: 'ru',
                label: t.specMgmtTabRussian,
                children: (
                  <div className={style.tabContent}>
                    <LocalizationFields lang="ru" />
                  </div>
                ),
              },
              {
                key: 'kz',
                label: t.specMgmtTabKazakh,
                children: (
                  <div className={style.tabContent}>
                    <LocalizationFields lang="kz" />
                  </div>
                ),
              },
              {
                key: 'en',
                label: t.specMgmtTabEnglish,
                children: (
                  <div className={style.tabContent}>
                    <LocalizationFields lang="en" />
                  </div>
                ),
              },
            ]}
          />
        </Form>
      </Modal>
    </AdminLayout>
  );
}
